import type {
  AircraftControls, AreaVertex, CameraSpec, DockOption, DraftDock, DraftValidationIssue, DraftWaypoint, DroneProfile, EditorHeightMode, GeneratedMappingRoute,
  HeadingParam, MappingTemplate, NearGroundIssue, PayloadLens, PayloadProfile, RouteStatistics, TakeoffRefPoint, TurnParam, VirtualAircraft,
  WaylineDraft, WaylineTemplateType, WaypointAction, WaypointActionType, WaypointHeadingMode, WaypointTurnMode,
} from '../types/wayline-editor'

// Pure planning logic for the manual route editor: aircraft catalogue, defaults,
// geometry, 2D mapping strip generation, statistics and validation. No Vue, no
// Cesium and no network so it can run under the node test harness.

export const EARTH_RADIUS = 6378137
export const WAYLINE_NAME_MAX_LENGTH = 60
export const MAX_WAYPOINTS = 65535
export const MIN_AREA_VERTICES = 3
/** provide/inject key for the reactive draft shared by the editor panels. */
export const WAYLINE_DRAFT_KEY = Symbol('wayline-draft')
/** Safe takeoff height (ALT) used when a route is planned from a dock pad. */
export const DOCK_TAKEOFF_SECURITY_HEIGHT = 80
/** Default safe takeoff height for routes without a dock. */
export const DEFAULT_TAKEOFF_SECURITY_HEIGHT = 20
/** Waypoints closer than this to the sampled terrain raise a 近地提示. */
export const DEFAULT_MIN_AGL = 20

let idCounter = 0
export function nextEditorId (prefix = 'id'): string {
  idCounter += 1
  return `${prefix}-${Date.now().toString(36)}-${idCounter}`
}

// Published sensor formats: 4/3 CMOS 20MP (17.3 x 13 mm, 24 mm equivalent),
// 1/2-inch 12MP and 48MP (6.4 x 4.8 mm, 24 mm equivalent) and 640 x 512 thermal.
const FOUR_THIRDS_WIDE: CameraSpec = { sensorWidth: 17.3, sensorHeight: 13, focalLength: 12.29, imageWidth: 5280, imageHeight: 3956, minPhotoInterval: 0.7 }
const HALF_INCH_12MP_WIDE: CameraSpec = { sensorWidth: 6.4, sensorHeight: 4.8, focalLength: 4.5, imageWidth: 4000, imageHeight: 3000, minPhotoInterval: 0.7 }
const HALF_INCH_48MP_WIDE: CameraSpec = { sensorWidth: 6.4, sensorHeight: 4.8, focalLength: 4.4, imageWidth: 8000, imageHeight: 6000, minPhotoInterval: 0.7 }
const THERMAL_640: CameraSpec = { sensorWidth: 7.68, sensorHeight: 6.144, focalLength: 9.1, imageWidth: 640, imageHeight: 512, minPhotoInterval: 2 }
const THERMAL_640_M30T: CameraSpec = { sensorWidth: 7.68, sensorHeight: 6.144, focalLength: 13.5, imageWidth: 640, imageHeight: 512, minPhotoInterval: 2 }

function payload (key: string, name: string, payloadEnumValue: number, cameras: Partial<Record<PayloadLens, CameraSpec>>, zoomRange: [number, number] | null): PayloadProfile {
  return { key, name, payloadEnumValue, payloadSubEnumValue: 0, lenses: Object.keys(cameras) as PayloadLens[], cameras, zoomRange }
}

const M30_CAMERA = payload('1-52-0', 'M30 相机', 52, { wide: HALF_INCH_12MP_WIDE, zoom: HALF_INCH_48MP_WIDE }, [21, 75])
const M30T_CAMERA = payload('1-53-0', 'M30T 相机', 53, { wide: HALF_INCH_12MP_WIDE, zoom: HALF_INCH_48MP_WIDE, ir: THERMAL_640_M30T }, [21, 75])
const M3E_CAMERA = payload('1-66-0', 'Mavic 3E 相机', 66, { wide: FOUR_THIRDS_WIDE, zoom: HALF_INCH_12MP_WIDE }, [162, 162])
const M3T_CAMERA = payload('1-67-0', 'Mavic 3T 相机', 67, { wide: HALF_INCH_48MP_WIDE, zoom: HALF_INCH_12MP_WIDE, ir: THERMAL_640 }, [162, 162])
const M3D_CAMERA = payload('1-80-0', 'M3D 相机', 80, { wide: FOUR_THIRDS_WIDE, zoom: HALF_INCH_12MP_WIDE }, [162, 162])
const M3TD_CAMERA = payload('1-81-0', 'M3TD 相机', 81, { wide: HALF_INCH_48MP_WIDE, zoom: HALF_INCH_12MP_WIDE, ir: THERMAL_640 }, [162, 162])
const M4D_CAMERA = payload('1-98-0', 'M4D 相机', 98, { wide: FOUR_THIRDS_WIDE, zoom: HALF_INCH_48MP_WIDE }, [70, 168])
const M4TD_CAMERA = payload('1-99-0', 'M4TD 相机', 99, { wide: HALF_INCH_48MP_WIDE, zoom: HALF_INCH_48MP_WIDE, ir: THERMAL_640 }, [70, 168])

function drone (key: string, name: string, series: string, droneEnumValue: number, droneSubEnumValue: number, payloads: PayloadProfile[], maxFlightSpeed: number, gimbalPitchRange: [number, number], flags: Partial<DroneProfile> = {}): DroneProfile {
  return { key, name, series, droneEnumValue, droneSubEnumValue, payloads, maxFlightSpeed, gimbalPitchRange, supportsRisky: true, supportsPano: true, supportsOrientedShoot: true, ...flags }
}

/** Aircraft supported by the FlightHub 2 route library that this cloud also knows. */
export const DRONE_PROFILES: DroneProfile[] = [
  drone('0-67-0', 'M30', 'Matrice 30 系列', 67, 0, [M30_CAMERA], 15, [-120, 45]),
  drone('0-67-1', 'M30T', 'Matrice 30 系列', 67, 1, [M30T_CAMERA], 15, [-120, 45]),
  drone('0-91-0', 'M3D', 'Matrice 3D 系列', 91, 0, [M3D_CAMERA], 15, [-90, 30]),
  drone('0-91-1', 'M3TD', 'Matrice 3D 系列', 91, 1, [M3TD_CAMERA], 15, [-90, 30]),
  drone('0-100-0', 'M4D', 'Matrice 4D 系列', 100, 0, [M4D_CAMERA], 15, [-90, 30]),
  drone('0-100-1', 'M4TD', 'Matrice 4D 系列', 100, 1, [M4TD_CAMERA], 15, [-90, 30]),
  drone('0-77-0', 'Mavic 3E', 'Mavic 3 行业系列', 77, 0, [M3E_CAMERA], 15, [-90, 35], { supportsRisky: false, supportsPano: false }),
  drone('0-77-1', 'Mavic 3T', 'Mavic 3 行业系列', 77, 1, [M3T_CAMERA], 15, [-90, 35], { supportsRisky: false, supportsPano: false }),
]

export const DRONE_SERIES = Array.from(new Set(DRONE_PROFILES.map(profile => profile.series)))

export function findDroneProfile (key: string): DroneProfile | undefined {
  return DRONE_PROFILES.find(profile => profile.key === key)
}

export function findPayloadProfile (droneKey: string, payloadKey: string): PayloadProfile | undefined {
  const profile = findDroneProfile(droneKey)
  return profile?.payloads.find(item => item.key === payloadKey) || profile?.payloads[0]
}

export const LENS_LABELS: Record<PayloadLens, string> = { wide: '广角', zoom: '变焦', ir: '红外', visable: '可见光' }

export const TURN_MODE_OPTIONS: Array<{ value: WaypointTurnMode; label: string }> = [
  { value: 'toPointAndStopWithDiscontinuityCurvature', label: '直线飞行，到点停' },
  { value: 'toPointAndStopWithContinuityCurvature', label: '曲线飞行，到点停' },
  { value: 'toPointAndPassWithContinuityCurvature', label: '曲线飞行，过点不停' },
  { value: 'coordinateTurn', label: '协调转弯，提前转弯' },
]

export const HEADING_MODE_OPTIONS: Array<{ value: WaypointHeadingMode; label: string }> = [
  { value: 'followWayline', label: '沿航线方向' },
  { value: 'manually', label: '手动控制' },
  { value: 'fixed', label: '保持当前偏航' },
  { value: 'smoothTransition', label: '自定义（平滑过渡）' },
  { value: 'towardPOI', label: '朝向兴趣点' },
]

export const ACTION_LABELS: Record<WaypointActionType, string> = {
  takePhoto: '拍照',
  startRecord: '开始录像',
  stopRecord: '结束录像',
  hover: '悬停',
  gimbalRotate: '云台转动',
  rotateYaw: '飞行器偏航',
  zoom: '相机变焦',
  focus: '对焦',
  customDirName: '新建文件夹',
  panoShot: '全景拍照',
  orientedShoot: '定角拍照',
  startTimedShot: '开始等时间隔拍照',
  startDistanceShot: '开始等距间隔拍照',
  endIntervalShot: '结束间隔拍照',
}

export const ACTION_ORDER: WaypointActionType[] = [
  'takePhoto', 'orientedShoot', 'startRecord', 'stopRecord', 'startTimedShot', 'startDistanceShot', 'endIntervalShot',
  'hover', 'rotateYaw', 'gimbalRotate', 'zoom', 'focus', 'panoShot', 'customDirName',
]

export function defaultActionParams (type: WaypointActionType, draft: WaylineDraft, waypoint?: DraftWaypoint): Record<string, string | number | boolean> {
  const lens = draft.waypoint.imageFormat.join(',') || 'wide'
  switch (type) {
    case 'takePhoto': return { fileSuffix: '', payloadLensIndex: lens, useGlobalPayloadLensIndex: true }
    case 'startRecord': return { fileSuffix: '', payloadLensIndex: lens, useGlobalPayloadLensIndex: true }
    case 'stopRecord': return { payloadLensIndex: lens }
    case 'hover': return { hoverTime: 5 }
    case 'gimbalRotate': return { gimbalPitchRotateEnable: true, gimbalPitchRotateAngle: waypoint?.gimbalPitchAngle ?? -90, gimbalYawRotateEnable: false, gimbalYawRotateAngle: 0, gimbalRotateTimeEnable: false, gimbalRotateTime: 0 }
    case 'rotateYaw': return { aircraftHeading: 0, aircraftPathMode: 'clockwise' }
    case 'zoom': return { focalLength: findPayloadProfile(draft.droneModelKey, draft.payloadModelKey)?.zoomRange?.[0] ?? 24 }
    case 'focus': return { isPointFocus: true, focusX: 0.5, focusY: 0.5, focusRegionWidth: 0.2, focusRegionHeight: 0.2, isInfiniteFocus: false }
    case 'customDirName': return { directoryName: 'folder' }
    case 'panoShot': return { payloadLensIndex: lens, useGlobalPayloadLensIndex: true, panoShotSubMode: 'panoShot_360' }
    case 'orientedShoot': return {
      gimbalPitchRotateAngle: waypoint?.gimbalPitchAngle ?? -30,
      gimbalYawRotateAngle: 0,
      aircraftHeading: 0,
      focalLength: findPayloadProfile(draft.droneModelKey, draft.payloadModelKey)?.zoomRange?.[0] ?? 24,
      payloadLensIndex: lens,
      useGlobalPayloadLensIndex: true,
      orientedFileSuffix: '',
      orientedPhotoMode: 'normalPhoto',
    }
    case 'startTimedShot': return { interval: 2, payloadLensIndex: lens }
    case 'startDistanceShot': return { interval: 5, payloadLensIndex: lens }
    case 'endIntervalShot': return {}
  }
}

export function createAction (type: WaypointActionType, draft: WaylineDraft, waypoint?: DraftWaypoint): WaypointAction {
  return { id: nextEditorId('action'), type, params: defaultActionParams(type, draft, waypoint) }
}

export function defaultHeadingParam (): HeadingParam {
  return { waypointHeadingMode: 'followWayline', waypointHeadingAngle: 0, waypointPoiPoint: null, waypointHeadingPathMode: 'followBadArc' }
}

export function defaultTurnParam (): TurnParam {
  return { waypointTurnMode: 'toPointAndStopWithDiscontinuityCurvature', waypointTurnDampingDist: 0, useStraightLine: true }
}

export interface CreateDraftOptions {
  name: string
  templateType: WaylineTemplateType
  droneModelKey: string
  payloadModelKey: string
  author: string
  /** Dock the route is planned for; its pad becomes the reference takeoff point. */
  dock?: DraftDock | null
  takeOffRefPoint?: TakeoffRefPoint | null
}

export function createDraft (options: CreateDraftOptions): WaylineDraft {
  const now = Date.now()
  const payloadProfile = findPayloadProfile(options.droneModelKey, options.payloadModelKey)
  const lens = payloadProfile?.lenses.includes('wide') ? 'wide' : payloadProfile?.lenses[0] || 'wide'
  const dock = options.dock || null
  const draft: WaylineDraft = {
    name: options.name,
    templateType: options.templateType,
    dock,
    droneModelKey: options.droneModelKey,
    payloadModelKey: payloadProfile?.key || options.payloadModelKey,
    payloadPositionIndex: 0,
    missionConfig: {
      flyToWaylineMode: 'safely',
      finishAction: 'goHome',
      exitOnRCLost: 'executeLostAction',
      executeRCLostAction: 'goBack',
      // A dock pad sits among buildings and trees, so the dock default climbs
      // well clear of them before heading to the first waypoint.
      takeOffSecurityHeight: dock ? DOCK_TAKEOFF_SECURITY_HEIGHT : DEFAULT_TAKEOFF_SECURITY_HEIGHT,
      globalTransitionalSpeed: 10,
      globalRTHHeight: 100,
      takeOffRefPoint: options.takeOffRefPoint ? { ...options.takeOffRefPoint } : null,
    },
    waypoint: {
      autoFlightSpeed: 10,
      heightMode: 'relativeToStartPoint',
      globalHeight: 100,
      globalWaypointTurnMode: 'toPointAndStopWithDiscontinuityCurvature',
      globalUseStraightLine: true,
      // Attitude recorded from the virtual aircraft only takes effect per point.
      gimbalPitchMode: 'usePointSetting',
      globalHeadingParam: defaultHeadingParam(),
      imageFormat: [lens],
      waypoints: [],
    },
    mapping: {
      autoFlightSpeed: 15,
      heightMode: 'relativeToStartPoint',
      height: 100,
      gsd: 0,
      direction: 0,
      margin: 0,
      orthoCameraOverlapH: 80,
      orthoCameraOverlapW: 70,
      shootType: 'time',
      elevationOptimizeEnable: true,
      gimbalPitchMode: 'manual',
      gimbalPitchAngle: -90,
      mappingHeadingMode: 'followWayline',
      mappingHeadingAngle: 0,
      imageFormat: [lens],
      lens,
      vertices: [],
      startVertexIndex: 0,
    },
    author: options.author,
    createTime: now,
    updateTime: now,
  }
  draft.mapping.gsd = round(heightToGsd(draft.mapping.height, cameraSpec(draft)), 2)
  return draft
}

export function createWaypoint (draft: WaylineDraft, longitude: number, latitude: number, height = draft.waypoint.globalHeight): DraftWaypoint {
  return {
    id: nextEditorId('wp'),
    longitude,
    latitude,
    height,
    useGlobalHeight: height === draft.waypoint.globalHeight,
    speed: draft.waypoint.autoFlightSpeed,
    useGlobalSpeed: true,
    useGlobalHeadingParam: true,
    headingParam: { ...draft.waypoint.globalHeadingParam, waypointPoiPoint: draft.waypoint.globalHeadingParam.waypointPoiPoint ? [...draft.waypoint.globalHeadingParam.waypointPoiPoint] as [number, number] : null },
    useGlobalTurnParam: true,
    turnParam: { waypointTurnMode: draft.waypoint.globalWaypointTurnMode, waypointTurnDampingDist: 0, useStraightLine: draft.waypoint.globalUseStraightLine },
    gimbalPitchAngle: -90,
    isRisky: false,
    actions: [],
  }
}

export function createVertex (longitude: number, latitude: number): AreaVertex {
  return { id: nextEditorId('vx'), longitude, latitude }
}

/** Reverse waypoint order and swap paired start/stop actions as FlightHub 2 does. */
export function reverseWaypoints (waypoints: DraftWaypoint[]): DraftWaypoint[] {
  const swap: Partial<Record<WaypointActionType, WaypointActionType>> = {
    startRecord: 'stopRecord',
    stopRecord: 'startRecord',
  }
  const reversed = [...waypoints].reverse()
  const intervalStarts = waypoints.flatMap(item => item.actions.filter(action => action.type === 'startTimedShot' || action.type === 'startDistanceShot'))
  return reversed.map(item => ({
    ...item,
    actions: item.actions.map(action => {
      if (swap[action.type]) return { ...action, type: swap[action.type]! }
      if (action.type === 'endIntervalShot' && intervalStarts.length) {
        const start = intervalStarts.shift()!
        return { ...action, type: start.type, params: { ...start.params } }
      }
      if (action.type === 'startTimedShot' || action.type === 'startDistanceShot') {
        return { ...action, type: 'endIntervalShot', params: {} }
      }
      return action
    }),
  }))
}

// ---------------------------------------------------------------------------
// Geometry

export function toRadians (degrees: number): number { return degrees * Math.PI / 180 }
export function toDegrees (radians: number): number { return radians * 180 / Math.PI }
export function round (value: number, digits = 3): number { const factor = 10 ** digits; return Math.round(value * factor) / factor }

export function haversineDistance (a: { longitude: number; latitude: number }, b: { longitude: number; latitude: number }): number {
  const dLat = toRadians(b.latitude - a.latitude)
  const dLng = toRadians(b.longitude - a.longitude)
  const sinLat = Math.sin(dLat / 2)
  const sinLng = Math.sin(dLng / 2)
  const h = sinLat * sinLat + Math.cos(toRadians(a.latitude)) * Math.cos(toRadians(b.latitude)) * sinLng * sinLng
  return 2 * EARTH_RADIUS * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** Initial bearing from a to b in degrees, 0 = north, clockwise. */
export function bearing (a: { longitude: number; latitude: number }, b: { longitude: number; latitude: number }): number {
  const lat1 = toRadians(a.latitude)
  const lat2 = toRadians(b.latitude)
  const dLng = toRadians(b.longitude - a.longitude)
  const y = Math.sin(dLng) * Math.cos(lat2)
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng)
  return (toDegrees(Math.atan2(y, x)) + 360) % 360
}

/** Local equidistant projection around an origin; metres east (x) and north (y). */
export function createLocalProjection (origin: { longitude: number; latitude: number }) {
  const cosLat = Math.cos(toRadians(origin.latitude))
  return {
    toLocal (point: { longitude: number; latitude: number }): [number, number] {
      return [toRadians(point.longitude - origin.longitude) * EARTH_RADIUS * cosLat, toRadians(point.latitude - origin.latitude) * EARTH_RADIUS]
    },
    toGeo (point: [number, number]): { longitude: number; latitude: number } {
      return {
        longitude: origin.longitude + toDegrees(point[0] / (EARTH_RADIUS * cosLat)),
        latitude: origin.latitude + toDegrees(point[1] / EARTH_RADIUS),
      }
    },
  }
}

/** Offset a WGS84 point by metres along a bearing (0 = north, clockwise). */
export function destinationPoint (point: { longitude: number; latitude: number }, bearingDegrees: number, metres: number): { longitude: number; latitude: number } {
  const radians = toRadians(bearingDegrees)
  const north = Math.cos(radians) * metres
  const east = Math.sin(radians) * metres
  const latitude = point.latitude + toDegrees(north / EARTH_RADIUS)
  const longitude = point.longitude + toDegrees(east / (EARTH_RADIUS * Math.cos(toRadians(point.latitude))))
  return { longitude: ((longitude + 540) % 360) - 180, latitude: Math.max(-90, Math.min(90, latitude)) }
}

export function polygonCentroid (vertices: Array<{ longitude: number; latitude: number }>): { longitude: number; latitude: number } {
  if (!vertices.length) return { longitude: 0, latitude: 0 }
  const sum = vertices.reduce((total, vertex) => ({ longitude: total.longitude + vertex.longitude, latitude: total.latitude + vertex.latitude }), { longitude: 0, latitude: 0 })
  return { longitude: sum.longitude / vertices.length, latitude: sum.latitude / vertices.length }
}

/** Signed shoelace area of a local polygon; positive when counter-clockwise. */
function signedArea (points: Array<[number, number]>): number {
  let area = 0
  for (let index = 0; index < points.length; index++) {
    const [x1, y1] = points[index]
    const [x2, y2] = points[(index + 1) % points.length]
    area += x1 * y2 - x2 * y1
  }
  return area / 2
}

export function polygonArea (vertices: Array<{ longitude: number; latitude: number }>): number {
  if (vertices.length < 3) return 0
  const projection = createLocalProjection(polygonCentroid(vertices))
  return Math.abs(signedArea(vertices.map(vertex => projection.toLocal(vertex))))
}

export function isPolygonSelfIntersecting (vertices: Array<{ longitude: number; latitude: number }>): boolean {
  if (vertices.length < 4) return false
  const projection = createLocalProjection(polygonCentroid(vertices))
  const points = vertices.map(vertex => projection.toLocal(vertex))
  const count = points.length
  for (let i = 0; i < count; i++) {
    for (let j = i + 1; j < count; j++) {
      if (Math.abs(i - j) <= 1 || (i === 0 && j === count - 1)) continue
      if (segmentsIntersect(points[i], points[(i + 1) % count], points[j], points[(j + 1) % count])) return true
    }
  }
  return false
}

function orientation (p: [number, number], q: [number, number], r: [number, number]): number {
  const value = (q[1] - p[1]) * (r[0] - q[0]) - (q[0] - p[0]) * (r[1] - q[1])
  if (Math.abs(value) < 1e-9) return 0
  return value > 0 ? 1 : 2
}

function segmentsIntersect (p1: [number, number], p2: [number, number], p3: [number, number], p4: [number, number]): boolean {
  const o1 = orientation(p1, p2, p3)
  const o2 = orientation(p1, p2, p4)
  const o3 = orientation(p3, p4, p1)
  const o4 = orientation(p3, p4, p2)
  return o1 !== o2 && o3 !== o4
}

/** Offset a simple polygon outward by distance metres (local coordinates). */
function expandPolygon (points: Array<[number, number]>, distance: number): Array<[number, number]> {
  if (distance <= 0 || points.length < 3) return points
  const ccw = signedArea(points) > 0
  const count = points.length
  const result: Array<[number, number]> = []
  for (let index = 0; index < count; index++) {
    const prev = points[(index - 1 + count) % count]
    const current = points[index]
    const next = points[(index + 1) % count]
    const n1 = edgeNormal(prev, current, ccw)
    const n2 = edgeNormal(current, next, ccw)
    const bisector: [number, number] = [n1[0] + n2[0], n1[1] + n2[1]]
    const length = Math.hypot(bisector[0], bisector[1]) || 1
    const dot = (n1[0] * bisector[0] + n1[1] * bisector[1]) / length
    const scale = distance / Math.max(0.3, dot)
    result.push([current[0] + bisector[0] / length * scale, current[1] + bisector[1] / length * scale])
  }
  return result
}

function edgeNormal (a: [number, number], b: [number, number], ccw: boolean): [number, number] {
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  const length = Math.hypot(dx, dy) || 1
  // Outward normal is to the right of the edge for CCW polygons.
  return ccw ? [dy / length, -dx / length] : [-dy / length, dx / length]
}

// ---------------------------------------------------------------------------
// Camera model

export function cameraSpec (draft: WaylineDraft): CameraSpec {
  const profile = findPayloadProfile(draft.droneModelKey, draft.payloadModelKey)
  return profile?.cameras[draft.mapping.lens] || profile?.cameras.wide || FOUR_THIRDS_WIDE
}

/** Ground sample distance in cm/px at a given height above the subject. */
export function heightToGsd (height: number, camera: CameraSpec): number {
  return camera.sensorWidth * height * 100 / (camera.focalLength * camera.imageWidth)
}

export function gsdToHeight (gsd: number, camera: CameraSpec): number {
  return gsd * camera.focalLength * camera.imageWidth / (camera.sensorWidth * 100)
}

/** Ground footprint in metres: width across the flight direction, length along it. */
export function footprint (height: number, camera: CameraSpec): { width: number; length: number } {
  return {
    width: camera.sensorWidth / camera.focalLength * height,
    length: camera.sensorHeight / camera.focalLength * height,
  }
}

// ---------------------------------------------------------------------------
// 2D mapping strip generation

export function generateMappingRoute (draft: WaylineDraft): GeneratedMappingRoute | null {
  const mapping = draft.mapping
  if (mapping.vertices.length < MIN_AREA_VERTICES) return null
  const camera = cameraSpec(draft)
  const size = footprint(mapping.height, camera)
  const lineSpacing = Math.max(1, size.width * (1 - mapping.orthoCameraOverlapW / 100))
  const photoSpacing = Math.max(0.5, size.length * (1 - mapping.orthoCameraOverlapH / 100))
  const projection = createLocalProjection(polygonCentroid(mapping.vertices))
  const local = mapping.vertices.map(vertex => projection.toLocal(vertex))
  const boundary = expandPolygon(local, mapping.margin)
  // Direction 0 means flight lines run north-south; rotate the polygon by
  // (90 - direction) so those lines become horizontal in the working frame.
  const frame = boundary.map(point => rotateBy(point, toRadians(90 - mapping.direction)))
  const minY = Math.min(...frame.map(point => point[1]))
  const maxY = Math.max(...frame.map(point => point[1]))
  const height = maxY - minY
  // Centre the strips inside the boundary; the half footprint on each side
  // covers the remaining edge, so no strip needs to lie outside the polygon.
  const lineCount = Math.max(1, Math.floor(height / lineSpacing + 1e-9) + 1)
  const offset = (height - (lineCount - 1) * lineSpacing) / 2
  const lines: Array<[[number, number], [number, number]]> = []
  for (let index = 0; index < lineCount; index++) {
    const y = minY + offset + index * lineSpacing
    const xs = polygonScanIntersections(frame, y)
    if (xs.length < 2) continue
    lines.push([[xs[0], y], [xs[xs.length - 1], y]])
  }
  if (!lines.length) return null
  // Serpentine ordering starting nearest to the requested start vertex.
  const start = rotateBy(boundary[Math.min(mapping.startVertexIndex, boundary.length - 1)] || boundary[0], toRadians(90 - mapping.direction))
  const startBottom = Math.abs(lines[0][0][1] - start[1]) <= Math.abs(lines[lines.length - 1][0][1] - start[1])
  const ordered = startBottom ? lines : [...lines].reverse()
  let leftToRight = Math.abs(ordered[0][0][0] - start[0]) <= Math.abs(ordered[0][1][0] - start[0])
  const points: Array<[number, number]> = []
  ordered.forEach(line => {
    const [a, b] = leftToRight ? line : [line[1], line[0]]
    points.push(a, b)
    leftToRight = !leftToRight
  })
  const unrotate = (point: [number, number]) => rotateBy(point, -toRadians(90 - mapping.direction))
  const waypoints = points.map(point => projection.toGeo(unrotate(point)))
  const photoCount = ordered.reduce((total, line) => total + Math.floor(Math.abs(line[1][0] - line[0][0]) / photoSpacing) + 1, 0)
  const triggerParam = mapping.shootType === 'distance'
    ? round(photoSpacing, 1)
    : round(Math.max(camera.minPhotoInterval, photoSpacing / Math.max(0.1, mapping.autoFlightSpeed)), 1)
  return {
    waypoints,
    triggerParam,
    lineSpacing: round(lineSpacing, 2),
    photoSpacing: round(photoSpacing, 2),
    photoCount,
    boundary: boundary.map(point => projection.toGeo(point)),
  }
}

function rotateBy (point: [number, number], angle: number): [number, number] {
  const cos = Math.cos(angle)
  const sin = Math.sin(angle)
  return [point[0] * cos - point[1] * sin, point[0] * sin + point[1] * cos]
}

function polygonScanIntersections (points: Array<[number, number]>, y: number): number[] {
  const xs: number[] = []
  const count = points.length
  for (let index = 0; index < count; index++) {
    const [x1, y1] = points[index]
    const [x2, y2] = points[(index + 1) % count]
    if ((y1 <= y && y2 > y) || (y2 <= y && y1 > y)) {
      xs.push(x1 + (y - y1) * (x2 - x1) / (y2 - y1))
    }
  }
  return xs.sort((a, b) => a - b)
}

// ---------------------------------------------------------------------------
// Statistics and validation

export function routeStatistics (draft: WaylineDraft, generated?: GeneratedMappingRoute | null): RouteStatistics {
  const takeoff = draft.missionConfig.takeOffRefPoint
  if (draft.templateType === 'mapping2d') {
    const route = generated === undefined ? generateMappingRoute(draft) : generated
    const points = route?.waypoints || []
    let distance = takeoff && points.length ? haversineDistance(takeoff, points[0]) : 0
    for (let index = 1; index < points.length; index++) distance += haversineDistance(points[index - 1], points[index])
    const speed = Math.max(0.1, draft.mapping.autoFlightSpeed)
    return {
      distance,
      duration: distance / speed + points.length * 2,
      waypointCount: points.length,
      photoCount: route?.photoCount || 0,
      area: polygonArea(draft.mapping.vertices),
    }
  }
  const points = draft.waypoint.waypoints
  let distance = takeoff && points.length ? haversineDistance(takeoff, points[0]) : 0
  let duration = takeoff && points.length ? distance / Math.max(0.1, draft.missionConfig.globalTransitionalSpeed) : 0
  let photoCount = 0
  for (let index = 0; index < points.length; index++) {
    const point = points[index]
    if (index > 0) {
      const previous = points[index - 1]
      const segment = Math.hypot(haversineDistance(previous, point), waypointHeight(draft, point) - waypointHeight(draft, previous))
      distance += segment
      duration += segment / Math.max(0.1, previous.useGlobalSpeed ? draft.waypoint.autoFlightSpeed : previous.speed)
    }
    point.actions.forEach(action => {
      if (action.type === 'takePhoto' || action.type === 'orientedShoot' || action.type === 'panoShot') photoCount += 1
      if (action.type === 'hover') duration += Number(action.params.hoverTime) || 0
      if (action.type === 'gimbalRotate' || action.type === 'rotateYaw' || action.type === 'zoom') duration += 2
    })
  }
  return { distance, duration, waypointCount: points.length, photoCount, area: 0 }
}

export function waypointHeight (draft: WaylineDraft, waypoint: DraftWaypoint): number {
  return waypoint.useGlobalHeight ? draft.waypoint.globalHeight : waypoint.height
}

export function formatDistance (metres: number): string {
  return metres >= 1000 ? `${(metres / 1000).toFixed(2)} km` : `${metres.toFixed(0)} m`
}

export function formatDuration (seconds: number): string {
  const total = Math.round(seconds)
  const minutes = Math.floor(total / 60)
  const rest = total % 60
  return minutes ? `${minutes} 分 ${rest} 秒` : `${rest} 秒`
}

export function formatArea (squareMetres: number): string {
  return squareMetres >= 10000 ? `${(squareMetres / 10000).toFixed(2)} 公顷` : `${squareMetres.toFixed(0)} m²`
}

export function validateDraft (draft: WaylineDraft): DraftValidationIssue[] {
  const issues: DraftValidationIssue[] = []
  const profile = findDroneProfile(draft.droneModelKey)
  if (!draft.name.trim()) issues.push({ level: 'error', message: '请输入航线名称。' })
  if (draft.name.trim().length > WAYLINE_NAME_MAX_LENGTH) issues.push({ level: 'error', message: `航线名称不能超过 ${WAYLINE_NAME_MAX_LENGTH} 个字符。` })
  if (!profile) issues.push({ level: 'error', message: '请选择支持的飞行器型号。' })
  const config = draft.missionConfig
  if (config.takeOffSecurityHeight < 8 || config.takeOffSecurityHeight > 1500) issues.push({ level: 'error', message: '安全起飞高度需在 8 到 1500 米之间。' })
  if (config.globalTransitionalSpeed <= 0 || config.globalTransitionalSpeed > 15) issues.push({ level: 'error', message: '全局过渡速度需在 0 到 15 m/s 之间。' })
  if (config.globalRTHHeight < 2 || config.globalRTHHeight > 1500) issues.push({ level: 'error', message: '返航高度需在 2 到 1500 米之间。' })
  const heightMode = draft.templateType === 'waypoint' ? draft.waypoint.heightMode : draft.mapping.heightMode
  if (!config.takeOffRefPoint) {
    issues.push({ level: heightMode === 'relativeToStartPoint' ? 'warning' : 'warning', message: '尚未设置参考起飞点；建议将机场设为参考起飞点以便预览三维高度。' })
  }
  const maxSpeed = profile?.maxFlightSpeed || 15
  if (draft.templateType === 'waypoint') {
    const template = draft.waypoint
    if (template.autoFlightSpeed <= 0 || template.autoFlightSpeed > maxSpeed) issues.push({ level: 'error', message: `全局航线速度需在 0 到 ${maxSpeed} m/s 之间。` })
    if (template.waypoints.length < 2) issues.push({ level: 'error', message: '航点航线至少需要 2 个航点。' })
    if (template.waypoints.length > MAX_WAYPOINTS) issues.push({ level: 'error', message: '航点数量超过上限。' })
    template.waypoints.forEach((waypoint, index) => {
      if (!waypoint.useGlobalSpeed && (waypoint.speed <= 0 || waypoint.speed > maxSpeed)) issues.push({ level: 'error', message: `航点 ${index + 1} 的速度需在 0 到 ${maxSpeed} m/s 之间。`, index })
      if (index > 0 && haversineDistance(template.waypoints[index - 1], waypoint) < 0.5 && Math.abs(waypointHeight(draft, waypoint) - waypointHeight(draft, template.waypoints[index - 1])) < 0.5) {
        issues.push({ level: 'warning', message: `航点 ${index} 与航点 ${index + 1} 位置重合。`, index })
      }
      const heading = waypoint.useGlobalHeadingParam ? template.globalHeadingParam : waypoint.headingParam
      if (heading.waypointHeadingMode === 'towardPOI' && !heading.waypointPoiPoint) issues.push({ level: 'error', message: `航点 ${index + 1} 使用“朝向兴趣点”模式，但未设置兴趣点。`, index })
      if (waypoint.isRisky && profile && !profile.supportsRisky) issues.push({ level: 'warning', message: `航点 ${index + 1} 的“不可返航点”不被 ${profile.name} 支持。`, index })
      waypoint.actions.forEach(action => {
        if (action.type === 'panoShot' && profile && !profile.supportsPano) issues.push({ level: 'error', message: `航点 ${index + 1} 的全景拍照不被 ${profile.name} 支持。`, index })
        if (action.type === 'hover' && !(Number(action.params.hoverTime) > 0)) issues.push({ level: 'error', message: `航点 ${index + 1} 的悬停时间需大于 0。`, index })
      })
    })
    const pairing = validateActionPairs(template.waypoints)
    issues.push(...pairing)
    if (template.globalHeadingParam.waypointHeadingMode === 'towardPOI' && !template.globalHeadingParam.waypointPoiPoint) {
      issues.push({ level: 'error', message: '全局偏航模式为“朝向兴趣点”，请在地图上设置兴趣点。' })
    }
  } else {
    const mapping = draft.mapping
    if (mapping.vertices.length < MIN_AREA_VERTICES) issues.push({ level: 'error', message: '测绘区域至少需要 3 个顶点。' })
    if (isPolygonSelfIntersecting(mapping.vertices)) issues.push({ level: 'error', message: '测绘区域边界不能自相交。' })
    if (mapping.autoFlightSpeed <= 0 || mapping.autoFlightSpeed > maxSpeed) issues.push({ level: 'error', message: `全局航线速度需在 0 到 ${maxSpeed} m/s 之间。` })
    if (mapping.height < 2 || mapping.height > 1500) issues.push({ level: 'error', message: '航线高度需在 2 到 1500 米之间。' })
    if (mapping.orthoCameraOverlapH < 10 || mapping.orthoCameraOverlapH > 90 || mapping.orthoCameraOverlapW < 10 || mapping.orthoCameraOverlapW > 90) issues.push({ level: 'error', message: '重叠率需在 10% 到 90% 之间。' })
    if (mapping.margin < 0 || mapping.margin > 500) issues.push({ level: 'error', message: '边距需在 0 到 500 米之间。' })
    if (mapping.vertices.length >= MIN_AREA_VERTICES && !isPolygonSelfIntersecting(mapping.vertices)) {
      const generated = generateMappingRoute(draft)
      if (!generated) issues.push({ level: 'error', message: '无法生成测绘航线，请调整区域或参数。' })
      else if (generated.waypoints.length > MAX_WAYPOINTS) issues.push({ level: 'error', message: '生成的航点数量超过上限，请缩小区域或提高航高。' })
      if (polygonArea(mapping.vertices) > 5_000_000) issues.push({ level: 'warning', message: '测绘区域较大，建议拆分为多个区域分别飞行。' })
    }
  }
  return issues
}

function validateActionPairs (waypoints: DraftWaypoint[]): DraftValidationIssue[] {
  const issues: DraftValidationIssue[] = []
  let recording = false
  let interval: { index: number } | null = null
  waypoints.forEach((waypoint, index) => {
    waypoint.actions.forEach(action => {
      if (action.type === 'startRecord') {
        if (recording) issues.push({ level: 'warning', message: `航点 ${index + 1} 重复开始录像，前一段录像尚未结束。`, index })
        recording = true
      } else if (action.type === 'stopRecord') {
        if (!recording) issues.push({ level: 'warning', message: `航点 ${index + 1} 结束录像前没有开始录像。`, index })
        recording = false
      } else if (action.type === 'startTimedShot' || action.type === 'startDistanceShot') {
        if (interval) issues.push({ level: 'error', message: `航点 ${index + 1} 开始间隔拍照前，请先结束航点 ${interval.index + 1} 的间隔拍照。`, index })
        interval = { index }
      } else if (action.type === 'endIntervalShot') {
        if (!interval) issues.push({ level: 'error', message: `航点 ${index + 1} 结束间隔拍照前没有开始间隔拍照。`, index })
        interval = null
      }
    })
  })
  if (interval) issues.push({ level: 'warning', message: '间隔拍照未在航线内结束，将持续到最后一个航点。' })
  return issues
}

export function hasErrors (issues: DraftValidationIssue[]): boolean {
  return issues.some(issue => issue.level === 'error')
}

/** Absolute WGS84 heights for preview; null when the reference takeoff height is unknown. */
export function absoluteHeight (draft: WaylineDraft, height: number, heightMode: 'relativeToStartPoint' | 'WGS84'): number | null {
  if (heightMode === 'WGS84') return height
  const takeoff = draft.missionConfig.takeOffRefPoint
  if (!takeoff || takeoff.ellipsoidHeight === null) return null
  return takeoff.ellipsoidHeight + height
}

export function cloneDraft (draft: WaylineDraft): WaylineDraft {
  return JSON.parse(JSON.stringify(draft))
}

// ---------------------------------------------------------------------------
// Docks

/** Aircraft each dock generation ships with, used when the pairing is unknown. */
export const DOCK_DEFAULT_DRONE: Record<string, string> = {
  '3-1-0': '0-67-0', // DJI Dock + M30
  '3-2-0': '0-91-0', // DJI Dock 2 + M3D
  '3-3-0': '0-100-0', // DJI Dock 3 + M4D
}

export const DOCK_MODEL_NAMES: Record<string, string> = {
  '3-1-0': 'DJI Dock',
  '3-2-0': 'DJI Dock 2',
  '3-3-0': 'DJI Dock 3',
}

export function dockModelName (modelKey: string): string {
  return DOCK_MODEL_NAMES[modelKey] || `机场 ${modelKey}`
}

/**
 * Aircraft profile key for a dock: the paired drone when the cloud knows it,
 * else the aircraft that dock generation ships with. Null for docks whose
 * aircraft the editor cannot plan for.
 */
export function dockDroneModelKey (dockModelKey: string, pairedModelKey?: string | null): string | null {
  if (pairedModelKey && findDroneProfile(pairedModelKey)) return pairedModelKey
  const fallback = DOCK_DEFAULT_DRONE[dockModelKey]
  return fallback && findDroneProfile(fallback) ? fallback : null
}

/** Shape of a bound dock as the device API and dock OSD report it. */
export interface BoundDockLike {
  device_sn: string
  nickname?: string
  device_name?: string
  domain: number | string
  type: number
  sub_type?: number
  children?: Array<{ device_name?: string; nickname?: string; domain?: number | string; type?: number; sub_type?: number } | null | undefined> | { device_name?: string; nickname?: string; domain?: number | string; type?: number; sub_type?: number } | null
}

export interface DockOsdLike {
  longitude?: number
  latitude?: number
  height?: number
  sub_device?: { device_model_key?: string; device_online_status?: number } | null
}

/** Pad position from a dock OSD, or null when the dock has not reported one. */
export function dockPadPosition (osd?: DockOsdLike | null): TakeoffRefPoint | null {
  if (!osd || !Number.isFinite(osd.longitude) || !Number.isFinite(osd.latitude)) return null
  if (osd.longitude === 0 && osd.latitude === 0) return null
  // A dock stands on the ground, so its pad is 0 m above ground level.
  return { longitude: osd.longitude!, latitude: osd.latitude!, ellipsoidHeight: Number.isFinite(osd.height) ? osd.height! : null, aglHeight: 0 }
}

/** Editor view of a bound dock: model, paired aircraft and pad position. */
export function buildDockOption (device: BoundDockLike, osd?: DockOsdLike | null): DockOption {
  const modelKey = `${device.domain}-${device.type}-${device.sub_type ?? 0}`
  const children = Array.isArray(device.children) ? device.children.filter(Boolean) : device.children ? [device.children] : []
  const child = children.find(item => item && String(item.domain ?? '0') === '0') || null
  const pairedKey = osd?.sub_device?.device_model_key || (child && child.type !== undefined ? `0-${child.type}-${child.sub_type ?? 0}` : null)
  const droneModelKey = dockDroneModelKey(modelKey, pairedKey)
  const profile = droneModelKey ? findDroneProfile(droneModelKey) : undefined
  return {
    sn: device.device_sn,
    name: device.nickname || device.device_name || device.device_sn,
    modelKey,
    modelName: dockModelName(modelKey),
    droneModelKey,
    droneName: profile?.name || (child ? child.nickname || child.device_name || '未知飞行器' : '未配对飞行器'),
    online: Boolean(osd),
    position: dockPadPosition(osd),
  }
}

// ---------------------------------------------------------------------------
// Height mode conversion

/**
 * Switch the height mode while keeping every waypoint where it is: ALT and
 * ellipsoid heights differ by the takeoff pad's ellipsoid height. Without a
 * known pad height the numbers are left unchanged.
 */
export function convertHeightMode (draft: WaylineDraft, next: EditorHeightMode): void {
  const current = draft.templateType === 'waypoint' ? draft.waypoint.heightMode : draft.mapping.heightMode
  if (current === next) return
  const pad = draft.missionConfig.takeOffRefPoint?.ellipsoidHeight ?? null
  const delta = pad === null ? 0 : (next === 'WGS84' ? pad : -pad)
  const shift = (height: number) => round(height + delta, 1)
  if (draft.templateType === 'waypoint') {
    draft.waypoint.heightMode = next
    draft.waypoint.globalHeight = shift(draft.waypoint.globalHeight)
    draft.waypoint.waypoints.forEach(waypoint => { waypoint.height = shift(waypoint.height) })
  } else {
    draft.mapping.heightMode = next
    draft.mapping.height = shift(draft.mapping.height)
  }
}

// ---------------------------------------------------------------------------
// Terrain clearance

/**
 * Waypoints whose height above the sampled terrain is below minAgl. Ground
 * heights are ellipsoid metres keyed by waypoint id; unknown ground is skipped.
 */
export function nearGroundIssues (draft: WaylineDraft, ground: Record<string, number | null | undefined>, minAgl = DEFAULT_MIN_AGL): NearGroundIssue[] {
  if (draft.templateType !== 'waypoint') return []
  const issues: NearGroundIssue[] = []
  draft.waypoint.waypoints.forEach((waypoint, index) => {
    const absolute = absoluteHeight(draft, waypointHeight(draft, waypoint), draft.waypoint.heightMode)
    const surface = ground[waypoint.id]
    if (absolute === null || surface === null || surface === undefined || !Number.isFinite(surface)) return
    // Compare at decimetre precision so a waypoint parked exactly on the
    // threshold (e.g. 20 m over a 0.00001 m pad) is not flagged.
    const agl = round(absolute - surface, 1)
    if (agl < minAgl) issues.push({ index, agl })
  })
  return issues
}

// ---------------------------------------------------------------------------
// Virtual aircraft

export type AircraftSpeedMode = 'slow' | 'normal' | 'fast'
export const AIRCRAFT_SPEED_MODES: Record<AircraftSpeedMode, { label: string; speed: number; next: AircraftSpeedMode }> = {
  slow: { label: '慢速', speed: 3, next: 'normal' },
  normal: { label: '中速', speed: 8, next: 'fast' },
  fast: { label: '快速', speed: 15, next: 'slow' },
}
export const AIRCRAFT_YAW_RATE = 60 // degrees per second
export const AIRCRAFT_CLIMB_RATE = 4 // metres per second
export const AIRCRAFT_GIMBAL_RATE = 45 // degrees per second

/** Normalise a heading to [0, 360). */
export function wrapHeading (degrees: number): number {
  return ((degrees % 360) + 360) % 360
}

/** Normalise a heading to (-180, 180], the range WPML uses for yaw angles. */
export function signedHeading (degrees: number): number {
  const wrapped = wrapHeading(degrees)
  return wrapped > 180 ? wrapped - 360 : wrapped
}

const COMPASS_POINTS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']

/** Compass label such as "NE 062°". */
export function headingLabel (degrees: number): string {
  const heading = wrapHeading(degrees)
  const point = COMPASS_POINTS[Math.round(heading / 45) % COMPASS_POINTS.length]
  return `${point} ${String(Math.round(heading) % 360).padStart(3, '0')}°`
}

/**
 * Advance the virtual aircraft by dt seconds. Forward/right move in the body
 * frame, climb changes height, yaw turns the nose, gimbal pitches the camera.
 */
export function stepVirtualAircraft (
  aircraft: VirtualAircraft,
  controls: AircraftControls,
  dt: number,
  options: { speed: number; heightRange: [number, number]; gimbalPitchRange: [number, number] },
): VirtualAircraft {
  if (dt <= 0) return aircraft
  const next: VirtualAircraft = { ...aircraft }
  if (controls.gimbal) {
    next.gimbalPitch = Math.min(options.gimbalPitchRange[1], Math.max(options.gimbalPitchRange[0], aircraft.gimbalPitch + controls.gimbal * AIRCRAFT_GIMBAL_RATE * dt))
  }
  if (controls.yaw) next.heading = wrapHeading(aircraft.heading + controls.yaw * AIRCRAFT_YAW_RATE * dt)
  const distance = Math.hypot(controls.forward, controls.right) * options.speed * dt
  if (distance > 0) {
    const direction = next.heading + toDegrees(Math.atan2(controls.right, controls.forward))
    const moved = destinationPoint(aircraft, direction, distance)
    next.longitude = moved.longitude
    next.latitude = moved.latitude
  }
  if (controls.climb) {
    next.height = Math.min(options.heightRange[1], Math.max(options.heightRange[0], aircraft.height + controls.climb * AIRCRAFT_CLIMB_RATE * dt))
  }
  return next
}

/**
 * Ground footprint of the camera: the lens frustum at the current heading and
 * gimbal pitch cut by a flat ground plane agl metres below the aircraft.
 * Returns the four corners near-left, far-left, far-right, near-right plus the
 * aim point, all as WGS84 positions.
 */
export function cameraFootprint (aircraft: VirtualAircraft, agl: number, camera: CameraSpec, focalLength = camera.focalLength) {
  const halfH = Math.atan(camera.sensorWidth / 2 / focalLength)
  const halfV = Math.atan(camera.sensorHeight / 2 / focalLength)
  const pitch = toRadians(aircraft.gimbalPitch)
  const height = Math.max(0.5, agl)
  const maxDistance = Math.min(600, height * 25)
  const groundDistance = (angle: number) => {
    const down = -angle // angle below the horizon
    return down <= 0.05 ? maxDistance : Math.min(maxDistance, height / Math.tan(down))
  }
  const near = groundDistance(pitch - halfV)
  const far = groundDistance(pitch + halfV)
  const corner = (distance: number, side: -1 | 1) => {
    const ahead = destinationPoint(aircraft, aircraft.heading, distance)
    const halfWidth = Math.hypot(distance, height) * Math.tan(halfH)
    return destinationPoint(ahead, aircraft.heading + side * 90, halfWidth)
  }
  const aimDistance = groundDistance(pitch)
  return {
    corners: [corner(near, -1), corner(far, -1), corner(far, 1), corner(near, 1)],
    aim: destinationPoint(aircraft, aircraft.heading, aimDistance),
    /** Slant range from the camera to the aim point in metres. */
    aimRange: Math.hypot(aimDistance, height),
    near,
    far,
  }
}

/** Whether the virtual aircraft is hovering at a waypoint (within tolerance). */
export function aircraftAtWaypoint (draft: WaylineDraft, aircraft: VirtualAircraft, waypoint: DraftWaypoint, tolerance = 0.5): boolean {
  return haversineDistance(aircraft, waypoint) <= tolerance && Math.abs(aircraft.height - waypointHeight(draft, waypoint)) <= tolerance
}

/** Human readable summary of a waypoint's actions, e.g. "拍照、悬停 5 s". */
export function describeActions (actions: WaypointAction[]): string {
  if (!actions.length) return '无动作'
  return actions.map(action => {
    if (action.type === 'hover') return `悬停 ${Number(action.params.hoverTime) || 0} s`
    if (action.type === 'rotateYaw') return `偏航 ${Number(action.params.aircraftHeading) || 0}°`
    if (action.type === 'zoom') return `变焦 ${Number(action.params.focalLength) || 0} mm`
    return ACTION_LABELS[action.type]
  }).join('、')
}
